import React, { Fragment, useState, useCallback, useMemo } from 'react';
import { Dialog } from '@headlessui/react';
import { Transition } from '@headlessui/react';
import { useParams } from 'react-router';
import { AdminLayout } from 'apps/sigma2-ui/src/components/AdminLayout';
import { AdminTable } from 'apps/sigma2-ui/src/components/AdminLayout';
import { AdminTableRow } from 'apps/sigma2-ui/src/components/AdminLayout';
import { Paragraph } from 'apps/sigma2-ui/src/components/ui/Paragraph';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { LegacyButton } from 'apps/sigma2-ui/src/components/ui/LegacyButton';
import { LegacyButtonVariant } from 'apps/sigma2-ui/src/components/ui/Buttons';
import { useSimpleReadPattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleReadPattern';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { Organization } from 'libs/end-to-end/src/types/organizations/organization';
import { Databases } from 'libs/end-to-end/src/types/databases/databases';
import { useNavigateSafe } from 'apps/sigma2-ui/src/lib/hooks/useNavigateSafe';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { EmptyObject } from 'libs/end-to-end/src/generic';
import { useAsyncCallback } from 'apps/sigma2-ui/src/lib/hooks/useAsyncCallback';
import { TextInput } from 'apps/sigma2-ui/src/components/ui/TextInput';
import { AdminBreadcrumbs } from 'apps/sigma2-ui/src/components/ui/AdminBreadcrumbs';

/**
 * DatabasesByOrganizationPage Component
 *
 * This component displays a list of databases for a specific organization.
 * It allows users to view, create, edit, and delete databases.
 */
export const DatabasesByOrganizationPage = () => {
  // State for managing delete database prompt
  const [deleteDatabasePrompt, setDeleteDatabasePrompt] = useState<
    string | null
  >(null);
  const [isDeletingDatabase, setIsDeletingDatabase] = useState(false);
  const { organizationId } = useParams();
  const navigate = useNavigateSafe();
  const { userAuthenticatedApiCall } = useUser();

  if (!organizationId) {
    throw new Error('organizationId is required');
  }

  const readOrgGetUrlFn = useCallback(() => {
    return `${getApiBaseUrl()}/organizations/${organizationId}`;
  }, [organizationId]);

  const readOrgPattern = useSimpleReadPattern({
    getUrlFn: readOrgGetUrlFn,
    responseSchema: Organization,
    canRead: true,
  });

  const patternGetUrlFn = useCallback(() => {
    return `${getApiBaseUrl()}/organizations/${organizationId}/databases`;
  }, [organizationId]);

  const pattern = useSimpleReadPattern({
    getUrlFn: patternGetUrlFn,
    responseSchema: Databases,
    canRead: true,
  });

  const refreshDatabases = useCallback(() => {
    if (pattern.status === 'done' || pattern.status === 'error') {
      pattern.reloadEntity();
    }
  }, [pattern]);

  /**
   * Handles the deletion of a database
   */
  const handleDeleteDatabase = useAsyncCallback(
    async (isAlive) => {
      if (deleteDatabasePrompt) {
        setIsDeletingDatabase(true);
        try {
          const deleteResult = await userAuthenticatedApiCall({
            url: `${getApiBaseUrl()}/organizations/${organizationId}/databases/${deleteDatabasePrompt}`,
            requestOptions: {
              method: 'DELETE',
            },
            responseSchema: EmptyObject,
          });

          if (isAlive()) {
            if (deleteResult && deleteResult.resultType === 'ok') {
              refreshDatabases();
            }
          }
          setDeleteConfirmationText('');
        } finally {
          if (isAlive()) {
            setIsDeletingDatabase(false);
            setDeleteDatabasePrompt(null);
          }
        }
      }
    },
    [
      organizationId,
      userAuthenticatedApiCall,
      refreshDatabases,
      deleteDatabasePrompt,
    ],
  );

  /**
   * Memoized values for organization name, new database link, and table rows
   */
  const { organizationName, newDatabaseLinkUrl, rows } = useMemo(() => {
    const databases = pattern.status === 'done' ? pattern.entity.databases : [];

    const rows: AdminTableRow[] = databases.map((database) => ({
      key: database.id,
      columns: [
        {
          key: 'name',
          columnType: 'text',
          text: database.name,
        },
        {
          key: 'description',
          columnType: 'text',
          text: database.description,
        },
        {
          key: 'edit',
          columnType: 'button',
          buttonText: 'Edit',
          onClick: () => {
            navigate(
              `/organizations/${organizationId}/databases/edit/${database.id}`,
            );
          },
        },
        {
          key: 'delete',
          columnType: 'button',
          buttonText: 'Delete',
          onClick: () => setDeleteDatabasePrompt(database.id),
          ariaLabel: `Delete ${database.name}`,
        },
      ],
    }));

    return {
      organizationName:
        readOrgPattern.status === 'done' ? readOrgPattern.entity.name : '',
      newDatabaseLinkUrl: `/organizations/${organizationId}/databases/new`,
      rows,
    };
  }, [organizationId, readOrgPattern, pattern, navigate]);

  // Add new state for the confirmation text input
  const [deleteConfirmationText, setDeleteConfirmationText] = useState('');

  return (
    <AdminLayout currentPage={'DatabasesByOrganization'}>
      <AdminBreadcrumbs
        items={[
          { label: 'Home', to: '/admin' },
          { label: 'Organizations', to: '/organizations' },
          {
            label: 'Databases',
            to: `/organizations/${organizationId}/databases`,
          },
        ]}
      />
      <Paragraph>
        Would you like to create a new database or manage an existing one?
      </Paragraph>
      <Paragraph>Organization: {organizationName}</Paragraph>

      <Spacing size={'sm'} direction={'horizontal'}>
        <Button
          behavior={{
            type: 'href',
            href: `/organizations/edit/${organizationId}`,
          }}
          text={'Back'}
        />

        <Button
          behavior={{
            type: 'href',
            href: newDatabaseLinkUrl,
          }}
          text={'Create New Database'}
        />
      </Spacing>

      <AdminTable
        headings={['Name', 'Description', 'Edit', 'Delete']}
        rows={rows}
      />

      <Transition appear show={!!deleteDatabasePrompt} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-overlay"
          onClose={() => {
            setDeleteDatabasePrompt(null);
            setDeleteConfirmationText(''); // Reset confirmation text when closing
          }}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-12px transition-all">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900"
                  >
                    Are you sure?
                  </Dialog.Title>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      Are you sure you want to delete this database? This action
                      cannot be undone. Type <strong>permanently delete</strong>{' '}
                      to confirm.
                    </p>
                    <TextInput
                      id="deleteConfirmText"
                      type="text"
                      name="deleteConfirmText"
                      data-qa="deleteConfirmText"
                      aria-label="deleteConfirmText"
                      value={deleteConfirmationText}
                      onChange={(e) =>
                        setDeleteConfirmationText(e.currentTarget.value)
                      }
                      placeholder="Type 'permanently delete'"
                      autoFocus
                      autoComplete="off"
                    />
                  </div>

                  <div className="mt-4">
                    <LegacyButton
                      variant={LegacyButtonVariant.PRIMARY_LIGHT}
                      onClick={handleDeleteDatabase}
                      disabled={
                        isDeletingDatabase ||
                        deleteConfirmationText !== 'permanently delete'
                      }
                      className="mt-4"
                    >
                      Delete
                    </LegacyButton>

                    <LegacyButton
                      variant={LegacyButtonVariant.PRIMARY_LIGHT}
                      onClick={() => {
                        setDeleteDatabasePrompt(null);
                        setDeleteConfirmationText(''); // Reset confirmation text when canceling
                      }}
                      disabled={isDeletingDatabase}
                      className="mt-4 ml-4"
                    >
                      Cancel
                    </LegacyButton>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </AdminLayout>
  );
};
